import { Euler, OrthographicCamera, Quaternion, Vector3 } from 'three';
import {
  Point2D,
  Point3D,
} from '../../../team-apis/otterdam/answers/shape-typings';
import { Axis } from '../../renderers/side-view/side-view.component';
import { CuboidLocationState3D } from '../../state/cuboid-location-state-3d';
import { EditSource } from '../../state/render-state';
import { getFaceDimensions } from '../../utils/cuboid-utils';
import { vectorToPoint } from '../../utils/point-cloud-utils';
import {
  addPoints,
  distanceBetween,
  scalePoint,
} from '../../utils/point-utils';

export class EditPanelCamera extends OrthographicCamera {
  private canvasSize: Point2D = [0, 0];

  constructor(private axis: Axis) {
    super(0, 0, 0, 0);
  }

  update(
    cuboidLocation: CuboidLocationState3D,
    editSource: EditSource | undefined,
    editPanelSize: Point2D,
  ) {
    this.canvasSize = editPanelSize;
    const isEditFromCurrentView = editSource?.index === this.axis;

    // Skipping updates for current view edits prevents issues where mouse
    // movements get increasingly large and unpredictable as the cuboid position
    // changes.
    // Updating only on external edits creates an effect where the point cloud
    // feels like moving behind the cuboid for the current view.
    if (isEditFromCurrentView) {
      return;
    }

    this.adjustPosition(cuboidLocation);
    this.adjustRotation(cuboidLocation);

    this.updateViewingFrustum(cuboidLocation);

    this.updateProjectionMatrix();
    this.updateMatrixWorld();
  }

  private adjustPosition(cuboidLocation: CuboidLocationState3D) {
    let translationDirecton: Point3D;

    if (this.axis === 0) {
      translationDirecton = transformToWorld([-1, 0, 0], cuboidLocation);
    } else if (this.axis === 1) {
      translationDirecton = transformToWorld([0, 1, 0], cuboidLocation);
    } else {
      translationDirecton = transformToWorld([0, 0, 1], cuboidLocation);
    }

    // Set the camera's position slightly further than the cuboid face, so that
    // rounding errors don't place the camera inside the cuboid. This fixes a
    // bug with raycasting that intermittently prevented dragging the cuboid.
    const extraDistance = 1e-4;
    const outwardsTranslation = scalePoint(
      translationDirecton,
      cuboidLocation.scale[this.axis] / 2 + extraDistance,
    );

    const cameraPosition = addPoints(
      cuboidLocation.position,
      outwardsTranslation,
    );
    this.position.fromArray(cameraPosition);
  }

  private adjustRotation(cuboidLocation: CuboidLocationState3D) {
    let viewUpDirection;
    if (this.axis !== 2) {
      viewUpDirection = transformToWorld([0, 0, 1], cuboidLocation);
    } else {
      viewUpDirection = transformToWorld([0, -1, 0], cuboidLocation);
    }
    this.up.fromArray(viewUpDirection);
    this.lookAt(...cuboidLocation.position);
  }

  private updateViewingFrustum({ position, scale }: CuboidLocationState3D) {
    const [widthOffset, heightOffset] = this.getCameraDimensions(scale);

    this.left = -widthOffset;
    this.right = widthOffset;
    this.top = heightOffset;
    this.bottom = -heightOffset;

    const distanceFromCuboidCenter = distanceBetween(
      this.position.toArray(),
      position,
    );
    const farFaceDistance = distanceFromCuboidCenter * 2;
    this.near = 0;
    this.far = farFaceDistance;
  }

  private getCameraDimensions(cuboidScale: Point3D): Point2D {
    // We assume that scales are always positive
    const [faceWidth, faceHeight] = getFaceDimensions(cuboidScale, this.axis);

    let widthOffset;
    let heightOffset;
    const extraPadding = 1.4;

    const viewAspect = this.canvasSize[0] / this.canvasSize[1];
    const faceAspect = faceWidth / faceHeight;
    if (faceAspect > viewAspect) {
      // Width dominates, so base frustum on width and adjust height
      widthOffset = (faceWidth * extraPadding) / 2;
      heightOffset = widthOffset / viewAspect;
    } else {
      // Height dominates, so base frustum on height and adjust width
      heightOffset = (faceHeight * extraPadding) / 2;
      widthOffset = heightOffset * viewAspect;
    }

    return [widthOffset, heightOffset];
  }
}

const reUsableEuler = new Euler();
const reUsableQuaternion = new Quaternion();
const reUsableVector = new Vector3();

export function transformToWorld(
  cuboidLocalDirection: Point3D,
  cuboidLocation: CuboidLocationState3D,
) {
  reUsableEuler.fromArray(cuboidLocation.rotation);
  reUsableQuaternion.setFromEuler(reUsableEuler);

  return vectorToPoint(
    reUsableVector
      .fromArray(cuboidLocalDirection)
      .applyQuaternion(reUsableQuaternion),
  );
}
